/**
 * Character-based text truncate / expand for the Text section block.
 * Full rich text stays in the DOM for SEO; JS only toggles preview vs full view.
 * The collapsed preview preserves HTML tags (h2, p, a, strong, lists, etc.).
 */
(function () {
  'use strict';

  var SELECTORS = {
    full: '[data-text-truncate-full]',
    preview: '[data-text-truncate-preview]',
    toggle: '[data-text-truncate-toggle]',
    labelMore: '[data-text-truncate-more]',
    labelLess: '[data-text-truncate-less]',
  };

  var ATTR = {
    maxChars: 'data-max-chars',
    initialized: 'data-text-truncate-initialized',
  };

  var CLASSES = {
    enabled: 'is-enabled',
    collapsed: 'is-collapsed',
    ready: 'is-ready',
  };

  var BLOCK_TAGS = {
    P: true,
    DIV: true,
    H1: true,
    H2: true,
    H3: true,
    H4: true,
    H5: true,
    H6: true,
    LI: true,
    UL: true,
    OL: true,
    BLOCKQUOTE: true,
    SECTION: true,
    ARTICLE: true,
    HEADER: true,
    FOOTER: true,
    FIGURE: true,
    FIGCAPTION: true,
    DT: true,
    DD: true,
    TR: true,
    PRE: true,
  };

  function normalizeWhitespace(value) {
    return String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseMaxChars(element) {
    var value = parseInt(element.getAttribute(ATTR.maxChars), 10);

    if (isNaN(value) || value < 1) {
      return 470;
    }

    if (value < 100) {
      return 100;
    }

    if (value > 2000) {
      return 2000;
    }

    return value;
  }

  function fillElement(element, fragment) {
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }

    element.appendChild(fragment);
  }

  function consumeBlockBoundary(state) {
    if (!state.hasContent || state.lastWasSpace || state.remaining <= 0 || state.done) {
      return;
    }

    state.remaining -= 1;
    state.lastWasSpace = true;

    if (state.remaining <= 0) {
      state.done = true;
    }
  }

  function cloneTruncatedNode(node, state) {
    if (state.done) {
      return null;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      var collapsed = String(node.textContent || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ');

      if (state.lastWasSpace) {
        collapsed = collapsed.replace(/^\s+/, '');
      }

      if (!collapsed) {
        return null;
      }

      if (collapsed.length <= state.remaining) {
        state.remaining -= collapsed.length;
        state.hasContent = true;
        state.lastWasSpace = /\s$/.test(collapsed);
        return document.createTextNode(collapsed);
      }

      var cut = collapsed.slice(0, state.remaining);
      var lastSpace = cut.lastIndexOf(' ');

      if (lastSpace > 0) {
        cut = cut.slice(0, lastSpace);
      }

      cut = cut.replace(/\s+$/, '');
      state.remaining = 0;
      state.done = true;
      state.hasContent = true;
      state.lastWasSpace = true;

      return document.createTextNode(cut + '…');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    if (node.tagName === 'BR') {
      consumeBlockBoundary(state);

      if (state.done && !state.hasContent) {
        return null;
      }

      return document.createElement('br');
    }

    if (BLOCK_TAGS[node.tagName]) {
      consumeBlockBoundary(state);

      if (state.done) {
        return null;
      }
    }

    var clone = node.cloneNode(false);
    var child = node.firstChild;
    var appended = false;

    while (child && !state.done) {
      var clonedChild = cloneTruncatedNode(child, state);

      if (clonedChild) {
        clone.appendChild(clonedChild);
        appended = true;
      }

      child = child.nextSibling;
    }

    if (!appended && !clone.textContent) {
      return null;
    }

    return clone;
  }

  function buildHtmlPreview(sourceElement, maxChars) {
    var fullText = normalizeWhitespace(sourceElement.textContent);

    if (fullText.length <= maxChars) {
      return null;
    }

    var fragment = document.createDocumentFragment();
    var state = {
      remaining: maxChars,
      done: false,
      hasContent: false,
      lastWasSpace: true,
    };
    var child = sourceElement.firstChild;

    while (child && !state.done) {
      var clonedChild = cloneTruncatedNode(child, state);

      if (clonedChild) {
        fragment.appendChild(clonedChild);
      }

      child = child.nextSibling;
    }

    if (!fragment.childNodes.length) {
      return null;
    }

    return fragment;
  }

  class TextTruncate extends HTMLElement {
    connectedCallback() {
      if (this.getAttribute(ATTR.initialized) === 'true') {
        return;
      }

      this.full = this.querySelector(SELECTORS.full);
      this.preview = this.querySelector(SELECTORS.preview);
      this.toggle = this.querySelector(SELECTORS.toggle);
      this.labelMore = this.querySelector(SELECTORS.labelMore);
      this.labelLess = this.querySelector(SELECTORS.labelLess);

      if (!this.full || !this.preview || !this.toggle) {
        this.classList.add(CLASSES.ready);
        return;
      }

      this.onToggle = this.onToggle.bind(this);
      this.maxChars = parseMaxChars(this);
      this.previewFragment = buildHtmlPreview(this.full, this.maxChars);

      this.setAttribute(ATTR.initialized, 'true');

      if (!this.previewFragment) {
        this.classList.remove(CLASSES.enabled, CLASSES.collapsed);
        this.classList.add(CLASSES.ready);
        this.toggle.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'true');
        this.preview.hidden = true;
        this.full.removeAttribute('hidden');
        this.full.removeAttribute('aria-hidden');
        return;
      }

      fillElement(this.preview, this.previewFragment);
      this.classList.add(CLASSES.enabled, CLASSES.collapsed, CLASSES.ready);
      this.toggle.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.setCollapsedState(true);
      this.toggle.addEventListener('click', this.onToggle);
      this.updateLabels(false);
    }

    disconnectedCallback() {
      if (this.toggle && this.onToggle) {
        this.toggle.removeEventListener('click', this.onToggle);
      }
    }

    onToggle(event) {
      event.preventDefault();

      var nextExpanded = this.classList.contains(CLASSES.collapsed);

      this.classList.toggle(CLASSES.collapsed, !nextExpanded);
      this.toggle.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
      this.setCollapsedState(!nextExpanded);
      this.updateLabels(nextExpanded);
    }

    setCollapsedState(isCollapsed) {
      if (isCollapsed) {
        this.full.setAttribute('hidden', '');
        this.full.setAttribute('aria-hidden', 'true');
        this.preview.removeAttribute('hidden');
        this.preview.setAttribute('aria-hidden', 'false');
      } else {
        this.full.removeAttribute('hidden');
        this.full.setAttribute('aria-hidden', 'false');
        this.preview.setAttribute('hidden', '');
        this.preview.setAttribute('aria-hidden', 'true');
      }
    }

    updateLabels(isExpanded) {
      if (this.labelMore) {
        this.labelMore.hidden = isExpanded;
      }

      if (this.labelLess) {
        this.labelLess.hidden = !isExpanded;
      }
    }
  }

  function defineTextTruncate() {
    if (!customElements.get('text-truncate')) {
      customElements.define('text-truncate', TextTruncate);
    }
  }

  defineTextTruncate();

  document.addEventListener('shopify:section:load', defineTextTruncate);
  document.addEventListener('shopify:section:reorder', defineTextTruncate);
  document.addEventListener('shopify:block:select', defineTextTruncate);
})();
